import React from 'react';
import Layout from '../../components/layout/layout';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';
import Section from '../../components/sections/section';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import Button from '../../components/ui/button';
import SectionBackground from '../../components/sections/section-background';

export default function PourquoiPage() {
  return (
    <Layout>
      <WebPageSchema 
        page={{
          title: "Pourquoi choisir Recruteurs.io pour votre carrière | Cabinet RH innovant",
          description: "Recruteurs.io révolutionne l'expérience candidat : transparence, onboarding, coaching RH, IA, feedback candidat. Une approche humaine et engagée.",
          url: "https://recruteurs.io/candidat/pourquoi",
          lastReviewed: "2025-05-22"
        }}
      />

      <BreadcrumbSchema 
        items={[
          { name: "Accueil", url: "https://recruteurs.io" },
          { name: "Candidat", url: "https://recruteurs.io/candidat" },
          { name: "Pourquoi Recruteurs.io", url: "https://recruteurs.io/candidat/pourquoi" }
        ]}
      />

      <HeroTemplateCandidats
        page="pourquoi"
        title={
          <>
            Pourquoi choisir{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-yellow-400">
              recruteurs.io
            </span>{' '}
            pour votre carrière ?
          </>
        }
        description="Une expérience candidat unique, humaine et transparente."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-recruteurs.io.webp"
        cta1={{
          label: "Voir les offres disponibles",
          href: "/candidat/offres"
        }}
        cta2={{
  label: "📘 Lire notre manifeste RH",
  href: "/manifeste",
  variant: "outline"  // ← MANQUAIT !!!
}}
      />

      <SectionBackground>
        
        <section className="py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Être candidat ne devrait jamais signifier avancer dans l'obscurité.
            <br />C'est pourquoi nous avons conçu un parcours totalement transparent.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold mb-2">
                📖 On te montre les coulisses
              </h3>
              <p className="text-sm text-neutral-700">
                Tu sais où tu mets les pieds : process détaillé, infos sur l'équipe,
                environnement de travail... Pas de surprise, juste de la clarté.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold mb-2">
                🧭 Tu avances à ton rythme
              </h3>
              <p className="text-sm text-neutral-700">
                Tu accèdes à chaque étape du recrutement à ton rythme, avec un suivi clair et humain à chaque point de contact.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold mb-2">
                🤝 Tu es considéré comme un partenaire
              </h3>
              <p className="text-sm text-neutral-700">
                Tu n'es pas "un CV de plus" : on valorise ton parcours, tes questions, tes doutes. On joue carte sur table.
              </p>
            </div>
          </div>
        </section>

        <Section
          title="Un onboarding pour bien démarrer"
          description="Dès que tu signes, on t'équipe pour réussir : checklist de prise de poste, tutos vidéo, mini-guide de l'entreprise et accès direct à ton futur manager."
          bullets={[
            "⚡ Moins de stress à l'arrivée",
            "🧠 Meilleure prise en main du poste",
            "🚀 Intégration accélérée, impact immédiat"
          ]}
          image="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/onboarding-_1_.webp"
          reversed
        />

        <section className="py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Ils en parlent mieux que nous</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <blockquote className="italic text-neutral-800">
                "Un process hyper fluide, et surtout on m'a vraiment écouté. J'ai senti que mon avis comptait."
              </blockquote>
              <p className="mt-4 text-sm text-neutral-600">&mdash; Léo, recruté en 2024</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <blockquote className="italic text-neutral-800">
                "Recruteurs.io m'a accompagné comme si j'étais déjà dans l'entreprise. Super onboarding."
              </blockquote>
              <p className="mt-4 text-sm text-neutral-600">&mdash; Sarah, DRH aujourd'hui cliente</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-neutral-100 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Tes bénéfices concrets</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xl">
                🎯 <br /> Tu gagnes du temps sur ta recherche
              </p>
            </div>
            <div>
              <p className="text-xl">
                🤝 <br /> Tu as une vraie transparence sur le process
              </p>
            </div>
            <div>
              <p className="text-xl">
                📈 <br /> Tu as plus de chances d'intégrer une boîte qui te correspond
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Des outils pour t'aider à chaque étape</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-bold mb-2">
                🎓 Tutoriels & vidéos
              </h3>
              <p className="text-sm text-neutral-700">
                Des ressources pour comprendre ton poste, anticiper les attentes, poser les bonnes questions dès les premiers jours.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-bold mb-2">
                🧠 Outils IA gratuits
              </h3>
              <p className="text-sm text-neutral-700">
                Préparer un entretien, mieux structurer ton CV ou identifier tes soft skills : notre kit IA est là pour toi.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-bold mb-2">
                📬 Accès direct à ton coach RH
              </h3>
              <p className="text-sm text-neutral-700">
                Tu as une question ? Tu n'attends pas 3 jours. On est dispo sur Slack ou WhatsApp pour t'accompagner concrètement.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Une nouvelle façon de vivre le recrutement
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-neutral-700">
            Recruteurs.io propose une approche de recrutement innovante à destination des candidats en quête de sens, de transparence et de respect. Grâce à notre processus structuré, nos outils digitaux, notre IA embarquée et notre accompagnement humain, chaque candidat bénéficie d'un parcours clair, bienveillant et engageant. Que vous cherchiez un poste à Marseille, en télétravail ou ailleurs, nous vous aidons à faire le bon choix, au bon moment, avec les bonnes personnes. Parce que chaque rencontre compte.
          </p>
        </section>

        <section className="py-20 bg-neutral-100 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à rejoindre une entreprise qui joue franc-jeu ?
          </h2>
          <p className="mb-6 text-neutral-700">
            Postule dès maintenant ou découvre les postes ouverts. Ici, ton avenir ne se joue pas à pile ou face.
          </p>
          <Button href="/candidat/offres" variant="gradient" size="lg">
            Voir les offres disponibles
          </Button>
        </section>
      </SectionBackground>
    </Layout>
  );
}